import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// Otter Assist Phase 8 — the email-action confirmation (OAD-100).
//
// An emailed link only NAVIGATES. It carries no token and no authority: the need is re-resolved
// from the backend for the signed-in member, nothing happens on arrival, and the member must
// confirm explicitly before the existing authenticated endpoint performs the action.

const INTENTS = {
  help: {
    title: "Let this member know you may be able to help?",
    body: "They'll be able to see your profile and reach out. You can withdraw shortly after.",
    cta: "Yes, I can help",
  },
  not_a_good_fit: {
    title: "Mark this need as not a good fit?",
    body: "It leaves your Community Needs inbox. You can undo this from Archived.",
    cta: "Not a good fit",
  },
  not_right_now: {
    title: "Set this need aside for now?",
    body: "It leaves your Community Needs inbox and stays in your history.",
    cta: "Not right now",
  },
};

export function OtterAssistEmailIntent({ deliveryId, intent, onClose }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const copy = INTENTS[intent];

  const { data, isLoading, isError } = useQuery({
    queryKey: ["oa-need-detail", deliveryId],
    queryFn: async () => (await api.get(`/otter-assist/community-needs/${deliveryId}`)).data,
    enabled: !!deliveryId && !!copy,
    retry: false,
  });
  const need = data?.need;

  const act = useMutation({
    mutationFn: async () => {
      if (intent === "help") {
        return (await api.post(`/otter-assist/assists/${need.assist_id}/help`,
          { confirm: true })).data;
      }
      return (await api.post(`/otter-assist/assists/${need.assist_id}/disposition`,
        { action: intent })).data;
    },
    onSuccess: () => {
      toast.success(intent === "help"
        ? "Thanks — they know you may be able to help."
        : "Thanks — we've taken it out of your inbox.");
      qc.invalidateQueries({ queryKey: ["oa-community-needs"] });
      qc.invalidateQueries({ queryKey: ["oa-unread-count"] });
      close();
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      toast.error(detail === "help_not_available"
        ? "This need is no longer open for help."
        : "That didn't work — please try again.");
    },
  });

  const close = () => { setOpen(false); onClose(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!copy) onClose(); }, [copy]);
  if (!copy) return null;

  const unavailable = isError || (!isLoading && !need);
  const alreadyOffered = need?.response === "offered";
  const alreadyDone = need?.response === "not_a_good_fit" || need?.response === "not_right_now";
  const closedNeed = need && need.assist_status !== "active";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="oa-email-intent-dialog">
        <DialogHeader>
          <DialogTitle data-testid="oa-email-intent-title">
            {unavailable ? "This need is no longer available" : copy.title}
          </DialogTitle>
          <DialogDescription data-testid="oa-email-intent-body">
            {unavailable
              ? "It may have closed, expired or been taken out of your inbox. Your other Community Needs are below."
              : copy.body}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-6" data-testid="oa-email-intent-loading">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : need ? (
          <div className="rounded-lg border border-border bg-secondary/30 p-3 text-sm"
            data-testid="oa-email-intent-need">
            <p className="font-[600] text-foreground">{need.poster?.display_name || "Member"}</p>
            <p className="mt-1 text-[13px] text-foreground/90">{need.summary}</p>
          </div>
        ) : null}

        {alreadyOffered && intent === "help" && (
          <p className="text-[13px] text-muted-foreground" data-testid="oa-email-intent-already">
            You've already offered to help with this need.
          </p>
        )}
        {alreadyDone && intent !== "help" && (
          <p className="text-[13px] text-muted-foreground" data-testid="oa-email-intent-done">
            You've already responded to this need.
          </p>
        )}
        {closedNeed && !unavailable && (
          <p className="text-[13px] text-muted-foreground" data-testid="oa-email-intent-closed">
            This need is no longer open for help.
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}
            data-testid="oa-email-intent-cancel">
            {unavailable ? "Close" : "Cancel"}
          </Button>
          {!unavailable && !alreadyOffered && !alreadyDone && !closedNeed && (
            <Button type="button" disabled={act.isPending || isLoading}
              onClick={() => act.mutate()} data-testid="oa-email-intent-confirm">
              {act.isPending ? "Working…" : copy.cta}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OtterAssistEmailIntent;
